import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Upload, X, LayoutDashboard, ChevronLeft, ChevronRight, Percent } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("feral_admin_token")}` },
});

const AVAIL = [
  { v: "auto", label: "Auto (use capacity)" },
  { v: "on", label: "On sale" },
  { v: "sold_out", label: "Sold out" },
  { v: "coming_soon", label: "Coming soon" },
];

const blankItem = () => ({
  id: `item-${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  price: 0,
  unit: "Ticket",
  capacity: null,
  availableOverride: "auto",
  showScarcity: false,
  scarcityLimit: 10,
  desc: "",
  addOnOnly: false,
  privateOnly: false,
});

const blankEvent = () => ({
  slug: "",
  name: "",
  type: "festival",
  tag: "",
  published: false,
  subtitle: "",
  dateLine: "",
  time: "",
  venue: "",
  priceFrom: "",
  limitedNote: "",
  accommodationNote: "",
  ageNote: "",
  images: [],
  description: [],
  dayTickets: [],
  weekendTickets: [],
  accommodation: [],
});

const inputCls =
  "w-full bg-white border border-[#d9c3a8] px-3 py-2 rounded-sm outline-none focus:ring-2 focus:ring-[#ce8a39] text-[#1e231f] text-sm";

/* ---- Field ---- */
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-[#7a6a58] mb-1">{label}</span>
    {children}
  </label>
);

/* ---- Image manager (multi-upload, delete, drag-to-reorder) ---- */
const ImageManager = ({ images, onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const list = images || [];
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" },
        });
        urls.push(data.url);
      }
      onChange([...list, ...urls]);
    } catch (err) {
      alert("Upload failed. Please try smaller images or try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const move = (from, to) => {
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };
  const reorder = (from, to) => {
    if (from == null || to == null || from === to || to < 0 || to >= list.length) return;
    const next = [...list];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };
  const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3">
        {list.map((u, i) => (
          <div
            key={i}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => { e.preventDefault(); if (overIndex !== i) setOverIndex(i); }}
            onDrop={(e) => { e.preventDefault(); reorder(dragIndex, i); setDragIndex(null); setOverIndex(null); }}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            className={`relative w-28 h-20 rounded overflow-hidden border cursor-move transition-all ${
              overIndex === i && dragIndex !== null && dragIndex !== i ? "ring-2 ring-[#ce8a39] border-[#ce8a39] scale-105" : "border-[#d9c3a8]"
            } ${dragIndex === i ? "opacity-40" : ""}`}
            data-testid={testId ? `${testId}-img-${i}` : undefined}
          >
            <img src={src(u)} alt="" draggable={false} className="w-full h-full object-cover pointer-events-none" />
            {i === 0 && (
              <span className="absolute bottom-1 left-1 bg-[#9b3d0a] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                MAIN
              </span>
            )}
            <div className="absolute inset-x-0 top-0 flex items-center justify-between p-1">
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => move(i, i - 1)}
                  disabled={i === 0}
                  title="Move left"
                  data-testid={testId ? `${testId}-left-${i}` : undefined}
                  className="bg-black/60 text-white rounded p-0.5 disabled:opacity-30"
                >
                  <ChevronLeft size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, i + 1)}
                  disabled={i === list.length - 1}
                  title="Move right"
                  data-testid={testId ? `${testId}-right-${i}` : undefined}
                  className="bg-black/60 text-white rounded p-0.5 disabled:opacity-30"
                >
                  <ChevronRight size={13} />
                </button>
              </div>
              <button
                type="button"
                onClick={() => onChange(list.filter((_, x) => x !== i))}
                title="Remove"
                data-testid={testId ? `${testId}-del-${i}` : undefined}
                className="bg-black/60 text-white rounded-full p-0.5"
              >
                <X size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <label
        className="inline-flex items-center gap-2 bg-[#ce8a39] hover:bg-[#b87a2e] text-white text-sm font-semibold px-4 py-2 rounded-full cursor-pointer transition-colors"
        data-testid={testId ? `${testId}-upload` : "upload-image-label"}
      >
        <Upload size={15} /> {uploading ? "Uploading…" : "Upload photos"}
        <input type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={uploading} />
      </label>
      {list.length > 0 && (
        <p className="text-xs text-[#9a876f] mt-2">
          Drag photos to reorder · the first photo is the main image · you can select several at once
        </p>
      )}
    </div>
  );
};

/* ---- Ticket editor ---- */
const TicketEditor = ({ title, items, onChange, withPhotos = false }) => {
  const update = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  return (
    <div className="bg-[#faf6ee] border border-[#e0d3bf] rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#9b3d0a]">{title}</h4>
        <button
          type="button"
          onClick={() => onChange([...items, blankItem()])}
          data-testid={`add-ticket-${title.replace(/\s+/g, "-").toLowerCase()}`}
          className="inline-flex items-center gap-1 text-sm text-[#9b3d0a] font-semibold hover:gap-1.5 transition-all"
        >
          <Plus size={15} /> Add
        </button>
      </div>
      {items.length === 0 && <p className="text-[#9a876f] text-sm">No items yet.</p>}
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="bg-white border border-[#e0d3bf] rounded p-3">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
              <div className="md:col-span-4">
                <Field label="Name">
                  <input className={inputCls} value={it.name} onChange={(e) => update(i, "name", e.target.value)} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Price (£)">
                  <input type="number" step="0.01" className={inputCls} value={it.price} onChange={(e) => update(i, "price", parseFloat(e.target.value) || 0)} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Unit">
                  <input className={inputCls} value={it.unit || ""} onChange={(e) => update(i, "unit", e.target.value)} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Capacity">
                  <input
                    type="number"
                    placeholder="∞"
                    className={inputCls}
                    value={it.capacity ?? ""}
                    onChange={(e) => update(i, "capacity", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Availability">
                  <select className={inputCls} value={it.availableOverride || "auto"} onChange={(e) => update(i, "availableOverride", e.target.value)}>
                    {AVAIL.map((a) => (
                      <option key={a.v} value={a.v}>{a.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2 gap-2">
              <input className={`${inputCls} flex-1`} placeholder="Short description (optional)" value={it.desc || it.blurb || ""} onChange={(e) => update(i, "desc", e.target.value)} />
              <div className="flex items-center gap-3">
                {it.sold != null && <span className="text-xs text-[#9a876f] whitespace-nowrap">Sold: {it.sold}</span>}
                <button type="button" onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-[#9b3d0a] hover:text-red-600">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {title !== "Accommodation" && (
              <div className="mt-3 border-t border-[#eee3d2] pt-3">
                <div className="flex flex-col md:flex-row md:items-end gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-[#6e5646] min-h-[40px]">
                    <input
                      type="checkbox"
                      checked={Boolean(it.showScarcity)}
                      onChange={(e) =>
                        update(i, "showScarcity", e.target.checked)
                      }
                    />
                    Show scarcity tracker
                  </label>

                  {it.showScarcity && (
                    <div className="w-full md:w-56">
                      <Field label="Starting remaining count">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          className={inputCls}
                          value={it.scarcityLimit ?? 10}
                          onChange={(e) =>
                            update(
                              i,
                              "scarcityLimit",
                              e.target.value === ""
                                ? null
                                : Math.max(
                                    1,
                                    parseInt(e.target.value, 10) || 1
                                  )
                            )
                          }
                        />
                      </Field>
                    </div>
                  )}
                </div>

                {it.showScarcity && (
                  <p className="text-xs text-[#9a876f] mt-2">
                    Current display:{" "}
                    <strong>
                      {Math.max(
                        0,
                        Number(it.scarcityLimit ?? 10) -
                          Number(it.sold || 0)
                      )}{" "}
                      remaining
                    </strong>
                    . This reduces automatically after paid ticket sales.
                  </p>
                )}
              </div>
            )}

            {title === "Accommodation" && (
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap gap-5">
                  <label className="flex items-center gap-2 text-sm text-[#6e5646]">
                    <input
                      type="checkbox"
                      checked={Boolean(it.addOnOnly)}
                      onChange={(e) => update(i, "addOnOnly", e.target.checked)}
                    />
                    Add-on only
                  </label>

                  <label className="flex items-center gap-2 text-sm text-[#6e5646]">
                    <input
                      type="checkbox"
                      checked={Boolean(it.privateOnly)}
                      onChange={(e) => update(i, "privateOnly", e.target.checked)}
                    />
                    Private link only
                  </label>
                </div>

                <Field label="Full description">
                  <textarea
                    className={`${inputCls} min-h-[90px] resize-y`}
                    placeholder="Full description shown in View room details & photos"
                    value={it.longDesc || ""}
                    onChange={(e) => update(i, "longDesc", e.target.value)}
                  />
                </Field>

                <Field label="What's included">
                  <textarea
                    className={`${inputCls} min-h-[110px] resize-y`}
                    placeholder="Enter one item per line"
                    value={(it.features || []).join("\n")}
                    onChange={(e) => update(i, "features", e.target.value.split("\n"))}
                    onBlur={(e) =>
                      update(
                        i,
                        "features",
                        e.target.value
                          .split("\n")
                          .map((x) => x.trim())
                          .filter(Boolean)
                      )
                    }
                  />
                  <p className="text-xs text-[#9a876f] mt-1">
                    Enter each included item on a separate line.
                  </p>
                </Field>
              </div>
            )}
            {withPhotos && (
              <div className="mt-3 border-t border-[#eee3d2] pt-3">
                <span className="block text-xs font-semibold text-[#7a6a58] mb-2">
                  Photos <span className="text-[#9a876f] font-normal">· first photo is the main thumbnail · use ‹ › to reorder</span>
                </span>
                <ImageManager
                  images={it.gallery || (it.image ? [it.image] : [])}
                  testId={`ticket-photos-${i}`}
                  onChange={(imgs) => {
                    const next = [...items];
                    next[i] = { ...next[i], gallery: imgs, image: imgs[0] || "" };
                    onChange(next);
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("list");
  const [draft, setDraft] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const guard = useCallback(
    (e) => {
      if (e?.response?.status === 401) {
        localStorage.removeItem("feral_admin_token");
        navigate("/admin/login");
      }
    },
    [navigate]
  );

  const load = useCallback(() => {
    axios.get(`${API}/admin/events`, authHeaders()).then((r) => setEvents(r.data)).catch(guard);
  }, [guard]);

  useEffect(() => {
    if (!localStorage.getItem("feral_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const edit = (ev) => {
    const next = JSON.parse(JSON.stringify(ev));
    delete next.discountCodes;
    setDraft(next);
    setIsNew(false);
    setView("edit");
    setMsg("");
  };
  const create = () => {
    setDraft(blankEvent());
    setIsNew(true);
    setView("edit");
    setMsg("");
  };

  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const save = async () => {
    if (!draft.slug || !draft.name) {
      setMsg("Slug and name are required.");
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      const mainImage = (draft.images || [])[0];
      const payload = { event: { ...draft, cardImage: mainImage || draft.cardImage || "" } };
      if (isNew) {
        await axios.post(`${API}/admin/events`, payload, authHeaders());
      } else {
        await axios.put(`${API}/admin/events/${draft.slug}`, payload, authHeaders());
      }
      load();
      setView("list");
    } catch (e) {
      guard(e);
      setMsg(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = (ev) => {
    setDeleteTarget(ev);
    setDeleteText("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/events/${deleteTarget.slug}`, authHeaders());
      setDeleteTarget(null);
      setDeleteText("");
      load();
    } catch (e) {
      guard(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4efe5]" data-testid="admin-events">
      <header className="bg-[#1c3c38] text-white px-6 py-4 flex items-center justify-between">
        <h1 className="feral-serif text-2xl">Event Manager</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => navigate("/admin/discounts")} className="inline-flex items-center gap-2 bg-[#ce8a39] hover:bg-[#b87a2e] px-4 py-2 rounded-full text-sm font-semibold transition-colors">
            <Percent size={16} /> Discounts
          </button>
          <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm transition-colors">
            <LayoutDashboard size={16} /> Dashboard
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {view === "list" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-[#6e5646]">Manage your events, tickets, prices, availability and photos.</p>
              <button onClick={create} data-testid="new-event-button" className="inline-flex items-center gap-2 bg-[#9b3d0a] hover:bg-[#7e3208] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors">
                <Plus size={16} /> New event
              </button>
            </div>
            <div className="space-y-4">
              {events.map((ev) => (
                <div key={ev.slug} className="bg-white border border-[#e0d3bf] rounded-lg p-5 flex items-center justify-between gap-4" data-testid={`event-row-${ev.slug}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-16 h-16 rounded overflow-hidden bg-[#f3e7d6] shrink-0">
                      {(ev.cardImage || (ev.images || [])[0]) && (
                        <img src={(ev.cardImage || ev.images[0]).startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${ev.cardImage || ev.images[0]}` : (ev.cardImage || ev.images[0])} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-[#1e231f] truncate">{ev.name}</p>
                      <p className="text-xs text-[#9a876f]">/{ev.slug} · {ev.type}</p>
                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${ev.published ? "bg-[#e6f0e2] text-[#4c6b3d]" : "bg-[#f0dbbe] text-[#9b3d0a]"}`}>
                        {ev.published ? "Published" : "Draft"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => edit(ev)} data-testid={`edit-event-${ev.slug}`} className="bg-[#9b3d0a] hover:bg-[#7e3208] text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors">Edit</button>
                    <button onClick={() => remove(ev)} data-testid={`delete-event-${ev.slug}`} title="Delete event" className="border border-[#d9c3a8] text-[#9b3d0a] hover:border-red-500 hover:text-red-600 px-3 py-2 rounded-full text-sm transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {view === "edit" && draft && (
          <div className="space-y-6" data-testid="event-editor">
            <button onClick={() => setView("list")} className="inline-flex items-center gap-1 text-[#9b3d0a] font-semibold text-sm">
              <ArrowLeft size={16} /> Back to events
            </button>

            <div className="bg-white border border-[#e0d3bf] rounded-lg p-6 space-y-4">
              <h3 className="feral-serif text-[#9b3d0a] text-2xl">{isNew ? "New event" : `Editing: ${draft.name}`}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Event name"><input className={inputCls} value={draft.name} onChange={(e) => set("name", e.target.value)} data-testid="event-name-input" /></Field>
                <Field label="Slug (URL)"><input className={inputCls} value={draft.slug} disabled={!isNew} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="event-slug-input" /></Field>
                <Field label="Subtitle"><input className={inputCls} value={draft.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} /></Field>
                <Field label="Card badge">
                  <input
                    className={inputCls}
                    value={draft.tag || ""}
                    placeholder="e.g. Networking"
                    onChange={(e) => set("tag", e.target.value)}
                  />
                </Field>
                <Field label="Category / description"><input className={inputCls} value={draft.category || ""} onChange={(e) => set("category", e.target.value)} /></Field>
                <Field label="Date line"><input className={inputCls} value={draft.dateLine || ""} onChange={(e) => set("dateLine", e.target.value)} /></Field>
                <Field label="Last day of event (hides it from upcoming events after this date)"><input type="date" className={inputCls} value={draft.endDate || ""} onChange={(e) => set("endDate", e.target.value)} data-testid="admin-event-end-date" /></Field>
                <Field label="Time"><input className={inputCls} value={draft.time || ""} onChange={(e) => set("time", e.target.value)} /></Field>
                <Field label="Venue"><input className={inputCls} value={draft.venue || ""} onChange={(e) => set("venue", e.target.value)} /></Field>
                <Field label="Price-from label"><input className={inputCls} value={draft.priceFrom || ""} onChange={(e) => set("priceFrom", e.target.value)} /></Field>
              </div>
              <Field label="Highlighted note (limited availability)"><input className={inputCls} value={draft.limitedNote || ""} onChange={(e) => set("limitedNote", e.target.value)} /></Field>
              <Field label="Description (one paragraph per line)">
                <textarea rows={5} className={inputCls} value={(draft.description || []).join("\n\n")} onChange={(e) => set("description", e.target.value.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean))} />
              </Field>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="pub" checked={!!draft.published} onChange={(e) => set("published", e.target.checked)} data-testid="event-published-checkbox" />
                <label htmlFor="pub" className="text-sm text-[#1e231f]">Published (visible on the website)</label>
              </div>
              <div className="border-t border-[#eee3d2] pt-4">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="waitlist" checked={!!draft.waitlistMode} onChange={(e) => set("waitlistMode", e.target.checked)} data-testid="event-waitlist-checkbox" />
                  <label htmlFor="waitlist" className="text-sm text-[#1e231f] font-semibold">Invite-only / Waitlist</label>
                </div>
                <p className="text-xs text-[#7a6a58] mt-1.5 ml-6 leading-relaxed">
                  When on, the public event page hides all prices and shows a "Join the waitlist" form instead of ticket sales.
                  {draft.slug && (
                    <>
                      {" "}Send invited people this private booking link to pay:{" "}
                      <span className="font-mono text-[#9b3d0a] break-all">/events/{draft.slug}/book</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="bg-white border border-[#e0d3bf] rounded-lg p-6">
              <h3 className="feral-serif text-[#9b3d0a] text-xl mb-3">Photos</h3>
              <ImageManager images={draft.images} onChange={(imgs) => set("images", imgs)} />
            </div>

            {draft.type === "community" ? (
              <div className="bg-white border border-[#e0d3bf] rounded-lg p-6 space-y-4">
                <h3 className="feral-serif text-[#9b3d0a] text-xl">Ticket</h3>
                <TicketEditor title="Parking / entry" items={draft.parking ? [draft.parking] : []} onChange={(items) => set("parking", items[0] || null)} withPhotos />
              </div>
            ) : (
              <div className="space-y-4">
                <TicketEditor title="Day tickets" items={draft.dayTickets || []} onChange={(v) => set("dayTickets", v)} withPhotos />
                <TicketEditor title="Weekend tickets" items={draft.weekendTickets || []} onChange={(v) => set("weekendTickets", v)} withPhotos />
                <TicketEditor title="Accommodation" items={draft.accommodation || []} onChange={(v) => set("accommodation", v)} withPhotos />
              </div>
            )}

            <div className="bg-white border border-[#e0d3bf] rounded-lg p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="feral-serif text-[#9b3d0a] text-xl">Discounts</h3>
                <p className="text-[#7a6a58] text-sm mt-1">Discount codes are managed centrally so one code can be used across selected events or the whole site.</p>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/admin/discounts?event=${encodeURIComponent(draft.slug)}`)}
                className="inline-flex items-center justify-center gap-2 bg-[#f3e7d6] hover:bg-[#ead7bf] text-[#9b3d0a] px-5 py-2.5 rounded-full text-sm font-semibold transition-colors shrink-0"
              >
                <Percent size={16} /> Manage discounts
              </button>
            </div>

            {msg && <p className="text-[#9b3d0a] text-sm bg-[#f3e7d6] border border-[#e0c9ad] rounded px-3 py-2" data-testid="editor-msg">{msg}</p>}
            <div className="flex items-center gap-3 sticky bottom-4">
              <button onClick={save} disabled={saving} data-testid="save-event-button" className="inline-flex items-center gap-2 bg-[#9b3d0a] hover:bg-[#7e3208] disabled:opacity-60 text-white px-6 py-3 rounded-full text-sm font-semibold shadow-lg transition-colors">
                <Save size={16} /> {saving ? "Saving…" : "Save event"}
              </button>
              <button onClick={() => setView("list")} className="text-[#6e5646] text-sm font-semibold">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation — must type the event name */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="delete-event-modal"
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="feral-serif text-[#9b3d0a] text-2xl mb-2">Delete this event?</h3>
            <p className="text-[#6e5646] text-sm leading-relaxed mb-4">
              This permanently deletes <strong className="text-[#1e231f]">{deleteTarget.name}</strong> and all its tickets, prices and photos. This <strong>cannot be undone</strong>.
            </p>
            <p className="text-sm text-[#1e231f] mb-2">
              To confirm, type the event name exactly:
              <span className="block mt-1 font-semibold text-[#9b3d0a]">{deleteTarget.name}</span>
            </p>
            <input
              autoFocus
              className={inputCls}
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="Type the event name to confirm"
              data-testid="delete-confirm-input"
            />
            <div className="flex items-center justify-end gap-3 mt-5">
              <button
                onClick={() => { setDeleteTarget(null); setDeleteText(""); }}
                data-testid="delete-cancel-button"
                className="px-4 py-2 rounded-full text-sm font-semibold text-[#6e5646] hover:bg-[#f3e7d6] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteText.trim() !== deleteTarget.name || deleting}
                data-testid="delete-confirm-button"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2 rounded-full text-sm font-semibold transition-colors"
              >
                <Trash2 size={15} /> {deleting ? "Deleting…" : "Delete event"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
